import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import NewDashboard from './pages/NewDashboard';
import AdminPanel from './pages/AdminPanel';
import PayscribePayment from './pages/PayscribePayment';
// import NewAdminPanel from './pages/NewAdminPanel';
import { Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache branding in localStorage to prevent color flash
const BRANDING_CACHE_KEY = 'app_branding_cache';

// Get cached branding or defaults
const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {}
  return null;
};

// Apply theme color immediately (before React renders)
const applyThemeColor = (color) => {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta && color) {
    themeColorMeta.setAttribute('content', color);
  }
};

// --- Branding -> design token bridge -----------------------------------------
// The admin panel stores colours as hex. The stylesheet drives everything from
// HSL triplet CSS variables. This converts one to the other so the admin colour
// picker keeps full control of --primary / --accent / --ring. Every other token
// stays fixed in index.css.
const hexToHslTriplet = (hex) => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let sat = 0;

  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / d + 2) / 6;
    else hue = ((r - g) / d + 4) / 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
};

// Readable foreground (white or near-black) for a given hex background.
const readableForeground = (hex) => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const toLin = (v) => {
    const s = parseInt(v, 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lum =
    0.2126 * toLin(h.slice(0, 2)) +
    0.7152 * toLin(h.slice(2, 4)) +
    0.0722 * toLin(h.slice(4, 6));
  return lum > 0.45 ? '224 18% 12%' : '0 0% 100%';
};

const applyBrandingTokens = (branding) => {
  if (!branding) return;
  const root = document.documentElement;

  const primary = hexToHslTriplet(branding.primary_color_hex);
  if (primary) {
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--ring', primary);
    const fg = readableForeground(branding.primary_color_hex);
    if (fg) root.style.setProperty('--primary-foreground', fg);
  }

  const accentHex = branding.accent_color_hex || branding.button_color_hex;
  const accent = hexToHslTriplet(accentHex);
  if (accent) {
    root.style.setProperty('--accent', accent);
    const fg = readableForeground(accentHex);
    if (fg) root.style.setProperty('--accent-foreground', fg);
  }
};

// Apply cached branding immediately on script load (before React)
const cachedBranding = getCachedBranding();
if (cachedBranding?.primary_color_hex) {
  applyThemeColor(cachedBranding.primary_color_hex);
}
// Paint tokens from cache immediately so there is no colour flash on load.
applyBrandingTokens(cachedBranding);

// Dynamic SEO Meta Tags Component
function DynamicSEO() {
  useEffect(() => {
    const loadSEO = async () => {
      try {
        const response = await axios.get(`${API}/api/public/branding`);
        const seo = response.data;
        
        // Cache branding for next page load (prevents flash)
        localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(seo));
        
        // Update theme-color for mobile address bar
        if (seo.primary_color_hex) {
          applyThemeColor(seo.primary_color_hex);
        }

        // Push the admin colours into the design tokens.
        applyBrandingTokens(seo);
        
        // Update document title
        if (seo.seo_site_title) {
          document.title = seo.seo_site_title;
        }
        
        // Update meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && seo.seo_meta_description) {
          metaDesc.setAttribute('content', seo.seo_meta_description);
        }
        
        // Update keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (seo.seo_keywords) {
          if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.name = 'keywords';
            document.head.appendChild(metaKeywords);
          }
          metaKeywords.setAttribute('content', seo.seo_keywords);
        }
        
        // Update author
        let metaAuthor = document.querySelector('meta[name="author"]');
        if (seo.seo_author) {
          if (!metaAuthor) {
            metaAuthor = document.createElement('meta');
            metaAuthor.name = 'author';
            document.head.appendChild(metaAuthor);
          }
          metaAuthor.setAttribute('content', seo.seo_author);
        }
        
        // Update OG tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle && seo.seo_site_title) {
          ogTitle.setAttribute('content', seo.seo_site_title);
        }
        
        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc && seo.seo_meta_description) {
          ogDesc.setAttribute('content', seo.seo_meta_description);
        }
        
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage && seo.seo_og_image) {
          ogImage.setAttribute('content', seo.seo_og_image);
        }
        
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl && seo.seo_canonical_url) {
          ogUrl.setAttribute('content', seo.seo_canonical_url);
        }
        
        // Update Twitter tags
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle && seo.seo_site_title) {
          twitterTitle.setAttribute('content', seo.seo_site_title);
        }
        
        const twitterDesc = document.querySelector('meta[name="twitter:description"]');
        if (twitterDesc && seo.seo_meta_description) {
          twitterDesc.setAttribute('content', seo.seo_meta_description);
        }
        
        const twitterImage = document.querySelector('meta[name="twitter:image"]');
        if (twitterImage && seo.seo_og_image) {
          twitterImage.setAttribute('content', seo.seo_og_image);
        }
        
        // Update canonical URL
        let canonical = document.querySelector('link[rel="canonical"]');
        if (seo.seo_canonical_url) {
          if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
          }
          canonical.setAttribute('href', seo.seo_canonical_url);
        }
        
      } catch (error) {
        console.log('Failed to load SEO settings:', error);
      }
    };
    
    loadSEO();
  }, []);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <DynamicSEO />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={!user ? <Landing setUser={setUser} /> : <Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={user ? <NewDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/admin" element={user?.is_admin ? <AdminPanel user={user} setUser={setUser} /> : <Navigate to="/dashboard" />} />
          <Route path="/payscribe-payment" element={user ? <PayscribePayment /> : <Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
